(() => {
  const nativePlay = HTMLMediaElement.prototype.play;
  let recentUserIntent = false;
  let intentTimer;

  const markUserIntent = () => {
    recentUserIntent = true;
    clearTimeout(intentTimer);
    intentTimer = setTimeout(() => { recentUserIntent = false; }, 1500);
  };

  document.addEventListener('pointerdown', markUserIntent, true);
  document.addEventListener('keydown', markUserIntent, true);

  HTMLMediaElement.prototype.play = function (...args) {
    if (!recentUserIntent) {
      this.autoplay = false;
      this.loop = false;
      this.removeAttribute('autoplay');
      this.removeAttribute('loop');
      try { this.pause(); } catch (_) {}
      return Promise.resolve();
    }
    return nativePlay.apply(this, args);
  };

  const defaultCopy = new Set([
    'Multicolumn',
    'Timeline',
    'Slideshow',
    'Image slide',
    'Video slide',
    'Video with text overlay',
    'Logo list',
    'Rich text',
    'Image with text',
    'Image with text overlay',
    'Multimedia collage',
    'Example title',
    'Add a tagline',
    'Button label',
    'Use this section to explain a set of product features, to link to a series of pages, or to answer common questions about your products.',
    'Use this text to share information about your brand with your customers. Describe a product, share announcements, or welcome customers to your store.',
    'Pair text with an image to focus on your chosen product, collection, or blog post. Add details on availability, style, or even provide a review.',
    'Share information about your brand with your customers. Describe a product, share announcements, or welcome customers to your store.'
  ]);

  const normalized = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const sectionByKey = (key) => document.querySelector(`[id*="${key}"]`);

  const replaceClasses = (el, remove, add) => {
    if (!el) return;
    remove.forEach((name) => el.classList.remove(name));
    add.forEach((name) => el.classList.add(name));
  };

  const setTextAlignment = (el, desktop = 'center', mobile = desktop) => {
    if (!el) return;
    [...el.classList].forEach((name) => {
      if (/^md:text-(left|center|right)$/.test(name) || /^text-(left|center|right)$/.test(name)) el.classList.remove(name);
    });
    el.classList.add(`md:text-${desktop}`, `text-${mobile}`);
  };

  const setBannerPosition = (section, desktopItems, desktopJustify, mobileItems, mobileJustify) => {
    const flex = section?.querySelector('.banner__content .page-width.w-full.h-full.flex');
    if (!flex) return;
    [...flex.classList].forEach((name) => {
      if (/^md:items-/.test(name) || /^md:justify-/.test(name) || /^items-/.test(name) || /^justify-/.test(name)) flex.classList.remove(name);
    });
    flex.classList.add(`md:items-${desktopItems}`, `md:justify-${desktopJustify}`, `items-${mobileItems}`, `justify-${mobileJustify}`);
  };

  const setResponsiveRatio = (section, desktopAdapt, mobileAdapt) => {
    if (!section || (!desktopAdapt && !mobileAdapt)) return;
    const img = section.querySelector('picture img');
    const source = section.querySelector('picture source[media]');
    const dw = Number(img?.getAttribute('width')) || img?.naturalWidth;
    const dh = Number(img?.getAttribute('height')) || img?.naturalHeight;
    const mw = Number(source?.getAttribute('width')) || dw;
    const mh = Number(source?.getAttribute('height')) || dh;
    if ((!dw || !dh) && (!mw || !mh)) return;

    let style = document.getElementById(`sublue-ratio-${section.id}`);
    if (!style) {
      style = document.createElement('style');
      style.id = `sublue-ratio-${section.id}`;
      document.head.appendChild(style);
    }
    const selector = `#${CSS.escape(section.id)}`;
    const desktopRule = desktopAdapt && dw && dh ? `${selector}{--ratio-percent:${(dh / dw) * 100}%;}` : '';
    const mobileRule = mobileAdapt && mw && mh ? `@media(max-width:767px){${selector}{--ratio-percent:${(mh / mw) * 100}%;}}` : '';
    style.textContent = desktopRule + mobileRule;
  };

  const setMediaHeight = (section, selector, desktop, mobile) => {
    const media = section?.querySelector(selector);
    if (!media) return;
    [...media.classList].forEach((name) => {
      if (/^media--/.test(name) || /^mobile:media--/.test(name)) media.classList.remove(name);
    });
    media.classList.add(`media--${desktop}`, `mobile:media--${mobile}`);
    setResponsiveRatio(section, desktop === 'adapt', mobile === 'adapt');
  };

  const setSectionPadding = (section, top, bottom) => {
    if (!section) return;
    if (top != null) section.style.setProperty('--section-padding-top', `${top}px`);
    if (bottom != null) section.style.setProperty('--section-padding-bottom', `${bottom}px`);
  };

  const tuneMulticolumn = (key, columns, paddingTop, paddingBottom) => {
    const section = sectionByKey(key);
    if (!section) return;
    const grid = section.querySelector('.multicolumn.card-grid');
    if (grid) {
      [...grid.classList].forEach((name) => { if (/^card-grid--\d+$/.test(name)) grid.classList.remove(name); });
      grid.classList.add(`card-grid--${columns}`);
    }
    const slider = section.querySelector('slider-element');
    slider?.classList.remove('slider--tablet');
    slider?.removeAttribute('tabindex');
    section.querySelectorAll('.multicolumn-card').forEach((card) => setTextAlignment(card, 'center', 'center'));
    section.querySelectorAll('.multicolumn-card__info .heading').forEach((heading) => {
      replaceClasses(heading, ['text-lg-2xl', 'text-2xl', 'lg:text-3xl'], ['text-base-xl']);
    });
    const sectionHeading = section.querySelector('.section-heading');
    if (sectionHeading && normalized(sectionHeading.textContent) === '') sectionHeading.style.display = 'none';
    setSectionPadding(section, paddingTop, paddingBottom);
  };

  const tuneOverlay = (key, config) => {
    const section = sectionByKey(key);
    if (!section) return;
    const heading = section.querySelector('.banner__title.heading');
    if (config.headingSize && heading) {
      replaceClasses(heading, ['title-sm','title-md','title-lg','title-xl','tracking-heading'], config.headingSize.split(' '));
    }
    const text = section.querySelector('.banner__box .rte');
    if (config.textSize && text) {
      replaceClasses(text, ['subtext-sm','subtext-md','subtext-lg','subtext-xl'], [config.textSize]);
    }
    if (config.desktopHeight && config.mobileHeight) setMediaHeight(section, '.banner', config.desktopHeight, config.mobileHeight);
    if (config.contentWidth) {
      const box = section.querySelector('.banner__box');
      if (box) {
        [...box.classList].forEach((name) => { if (/^banner__box--/.test(name)) box.classList.remove(name); });
        box.classList.add(`banner__box--${config.contentWidth}`);
      }
    }
    if (config.position) setBannerPosition(section, ...config.position);
    if (config.align) setTextAlignment(section.querySelector('.banner__box'), config.align[0], config.align[1]);
    if (config.overlayOpacity != null) {
      const overlay = section.querySelector('.banner__overlay');
      if (overlay) overlay.style.opacity = String(config.overlayOpacity / 100);
    }
  };

  const tuneImageWithText = (key, config) => {
    const section = sectionByKey(key);
    if (!section) return;
    if (config.desktopHeight && config.mobileHeight) setMediaHeight(section, '.image-with-text__image', config.desktopHeight, config.mobileHeight);
    const root = section.querySelector('.image-with-text');
    if (root && config.layout) {
      replaceClasses(root, ['lg:flex-row','lg:flex-row-reverse'], [config.layout === 'text_first' ? 'lg:flex-row-reverse' : 'lg:flex-row']);
    }
    const rich = section.querySelector('.image-with-text .rich-text');
    if (config.align) setTextAlignment(rich, config.align[0], config.align[1]);
    const heading = rich?.querySelector('.heading');
    if (config.headingSize && heading) replaceClasses(heading, ['title-sm','title-md','title-lg','title-xl','tracking-heading'], config.headingSize.split(' '));
    if (config.textSize) rich?.querySelectorAll('.rte').forEach((el) => replaceClasses(el, ['subtext-sm','subtext-md','subtext-lg','subtext-xl'], [config.textSize]));
    if (config.contentPosition) {
      const items = section.querySelectorAll('.image-with-text__item');
      const content = items[1];
      if (content) replaceClasses(content, ['align-self-start','align-self-center','align-self-end'], [config.contentPosition]);
    }
    if (config.largeImage) {
      const items = section.querySelectorAll('.image-with-text__item');
      items[1]?.classList.add('lg:grow-0');
    }
    setSectionPadding(section, config.paddingTop, config.paddingBottom);
  };

  const tuneMixPro = () => {
    const marker = sectionByKey('image_with_text_overlay_CGBhDr');
    if (!marker || !normalized(marker.textContent).includes('MixPro 2026')) return;

    setBannerPosition(marker, 'center', 'start', 'start', 'start');

    tuneOverlay('image_with_text_overlay_a7K3RR', {
      headingSize: 'title-sm', textSize: 'subtext-sm', desktopHeight: 'adapt', mobileHeight: '600px', contentWidth: 'large',
      position: ['start','center','start','center'], align: ['center','center'], overlayOpacity: 0
    });
    tuneMulticolumn('multicolumn_xagJwE', 4, 72, 72);

    tuneOverlay('image_with_text_overlay_gc9XUe', {
      headingSize: 'title-sm', desktopHeight: 'adapt', mobileHeight: 'adapt',
      position: ['start','center','start','center'], align: ['center','center'], overlayOpacity: 0
    });
    tuneMulticolumn('multicolumn_ibA9yG', 3, 36, 72);

    tuneOverlay('image_with_text_overlay_XJjaGr', {
      headingSize: 'title-sm', desktopHeight: '800px', mobileHeight: '500px',
      position: ['start','center','start','start'], align: ['center','center'], overlayOpacity: 0
    });
    tuneMulticolumn('multicolumn_gV3VyH', 3, 36, 72);

    const collage = sectionByKey('collage_bLzAcf');
    if (collage) {
      const heading = collage.querySelector('.section-heading .heading');
      if (heading) replaceClasses(heading, ['title-md','title-lg','title-xl','tracking-heading'], ['title-sm']);
      const sectionHeading = collage.querySelector('.section-heading');
      if (sectionHeading) setTextAlignment(sectionHeading, 'center', 'center');
      const media = collage.querySelectorAll('.collage .media');
      const mobileRatios = ['square','landscape','landscape'];
      media.forEach((el, index) => {
        [...el.classList].forEach((name) => { if (/^mobile:media--/.test(name)) el.classList.remove(name); });
        if (mobileRatios[index]) el.classList.add(`mobile:media--${mobileRatios[index]}`);
      });
    }

    tuneOverlay('image_with_text_overlay_xdgLmd', {
      desktopHeight: 'adapt', mobileHeight: '300px',
      position: ['end','start','center','center'], align: ['left','center']
    });

    tuneImageWithText('image_with_text_UCdaKG', {
      headingSize: 'title-sm', textSize: 'subtext-sm', desktopHeight: '500px', mobileHeight: 'adapt',
      layout: 'text_first', align: ['center','center'], contentPosition: 'align-self-center', largeImage: true, paddingTop: 72, paddingBottom: 72
    });

    tuneImageWithText('image_with_text_kQRcWW', {
      headingSize: 'title-sm', textSize: 'subtext-sm', desktopHeight: '500px', mobileHeight: '200px',
      layout: 'image_first', align: ['center','center'], contentPosition: 'align-self-center', paddingTop: 72, paddingBottom: 72
    });
    const upgrade = sectionByKey('image_with_text_kQRcWW');
    if (upgrade) {
      const rich = upgrade.querySelector('.rich-text');
      const imageWrap = [...(rich?.children || [])].find((el) => el.querySelector?.('img[src*="22_1c6f6d9f"]'));
      const texts = rich?.querySelectorAll('.rte');
      if (imageWrap && texts?.[0]) texts[0].after(imageWrap);
      const img = imageWrap?.querySelector('img');
      if (img) {
        img.style.removeProperty('--image-max-width');
        img.style.removeProperty('--image-max-width-mobile');
        img.style.width = '100%';
        img.style.maxWidth = '560px';
      }
    }

    tuneImageWithText('image_with_text_bWtiDq', {
      headingSize: 'title-sm', textSize: 'subtext-sm', desktopHeight: '500px', mobileHeight: '200px',
      layout: 'image_first', align: ['left','left'], contentPosition: 'align-self-start', paddingTop: 72, paddingBottom: 72
    });
    const classic = sectionByKey('image_with_text_bWtiDq');
    const classicBadge = classic?.querySelector('.rich-text img[src*="MIXpro_f0e13717"]');
    if (classicBadge) {
      classicBadge.style.setProperty('--image-max-width', '140px');
      classicBadge.style.setProperty('--image-max-width-mobile', '100px');
    }

    const richClassic = sectionByKey('rich_text_iNDBEF');
    if (richClassic) {
      const heading = richClassic.querySelector('.rich-text .heading');
      if (heading) replaceClasses(heading, ['title-md','title-lg','title-xl','tracking-heading'], ['title-sm']);
      setTextAlignment(richClassic.querySelector('.rich-text'), 'center', 'center');
      const texts = richClassic.querySelectorAll('.rich-text .rte');
      texts.forEach((el) => replaceClasses(el, ['subtext-md','subtext-lg','subtext-xl'], ['subtext-sm']));
      if (texts[0]) replaceClasses(texts[0], ['body'], ['heading']);
      setSectionPadding(richClassic, 72, 32);
    }

    const richDiff = sectionByKey('rich_text_kPFerm');
    if (richDiff) {
      const heading = richDiff.querySelector('.rich-text .heading');
      if (heading) replaceClasses(heading, ['title-md','title-lg','title-xl','tracking-heading'], ['title-sm']);
      setTextAlignment(richDiff.querySelector('.rich-text'), 'center', 'center');
      setSectionPadding(richDiff, 72, 0);
    }

    tuneImageWithText('image_with_text_aJEVCf', {
      desktopHeight: 'adapt', mobileHeight: 'auto', layout: 'image_first', align: ['left','left'], contentPosition: 'align-self-center', paddingTop: 72, paddingBottom: 72
    });

    tuneMulticolumn('multicolumn_jGGLMK', 4, 36, 36);

    const faq = sectionByKey('faq');
    if (faq) {
      const heading = faq.querySelector('.section-heading .heading');
      if (heading) replaceClasses(heading, ['title-md','title-lg','title-xl','tracking-heading'], ['title-sm']);
    }
  };

  const clean = (root = document) => {
    const queryRoot = root.querySelectorAll ? root : document;

    queryRoot.querySelectorAll('video').forEach((video) => {
      video.autoplay = false;
      video.loop = false;
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      if (!recentUserIntent) {
        try { video.pause(); } catch (_) {}
      }
    });

    queryRoot.querySelectorAll('video-media[autoplay], video-with-text[autoplay], [data-parallax]').forEach((el) => {
      el.removeAttribute('autoplay');
      el.removeAttribute('data-parallax');
      el.removeAttribute('data-parallax-dir');
    });

    queryRoot.querySelectorAll('slideshow-element[autoplay]').forEach((slider) => {
      slider.removeAttribute('autoplay');
      slider.removeAttribute('autoplay-speed');
    });

    queryRoot.querySelectorAll('[data-autoplay="true"], [data-auto-play="true"]').forEach((el) => {
      if (el.hasAttribute('data-autoplay')) el.setAttribute('data-autoplay', 'false');
      if (el.hasAttribute('data-auto-play')) el.setAttribute('data-auto-play', 'false');
    });

    queryRoot.querySelectorAll('logo-list').forEach((logoList) => {
      const replacement = document.createElement('div');
      replacement.className = 'logo-list-static';
      while (logoList.firstChild) replacement.appendChild(logoList.firstChild);
      replacement.querySelectorAll('.flex-nowrap').forEach((el) => {
        el.classList.remove('flex-nowrap');
        el.classList.add('flex-wrap');
      });
      logoList.replaceWith(replacement);
    });

    queryRoot.querySelectorAll('h1,h2,h3,h4,h5,h6,p,a,button,.rte').forEach((el) => {
      if (defaultCopy.has(normalized(el.textContent))) el.remove();
    });

    queryRoot.querySelectorAll('.section-heading').forEach((el) => {
      if (normalized(el.textContent) === '') el.style.display = 'none';
    });

    queryRoot.querySelectorAll('[aria-label="Slideshow about our brand"]').forEach((el) => {
      el.setAttribute('aria-label', 'Product gallery');
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) clean(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  clean(document);

  const start = () => {
    clean(document);
    tuneMixPro();
    requestAnimationFrame(() => tuneMixPro());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    clean(event.target);
    tuneMixPro();
  });
})();
